import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, Form, Button, Space, Switch, Card, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import { setAppTitle } from 'src/store/app';
import {
  automodesListSelector,
  automodesMetaDataSelector,
  fetchAutomodes,
  isLoadingFetchAutomodesSelector,
  updateAutomode,
  deleteAutomode
} from 'src/store/automode';

import { AutomodeModal } from './Modal/Modal';

import './automode.scss';

const INTERVAL_COLOR_MAP = {
  5: 'purple',
  15: 'blue',
  20: 'cyan',
  30: 'geekblue',
  60: 'green',
  120: 'gold'
};

export const Automode = () => {
  const { t } = useTranslation('automode');
  const dispatch = useDispatch();

  const data = useSelector(automodesListSelector);
  const automodesMeta = useSelector(automodesMetaDataSelector);
  const isLoading = useSelector(isLoadingFetchAutomodesSelector);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAutomode, setSelectedAutomode] = useState(null);
  const [automodePagination, setAutomodePagination] = useState({ page: 1, perPage: 10 });

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(setAppTitle(t('menu:automode')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchAutomodes(automodePagination));
  }, [dispatch, automodePagination]);

  const handlePageChange = useCallback((pagination) => {
    setAutomodePagination({
      page: pagination?.current || 1,
      perPage: pagination?.pageSize || 10
    });
  }, []);

  const openCreateModal = useCallback(() => {
    setSelectedAutomode(null);
    form.resetFields();
    form.setFieldsValue({
      message: '',
      intervalMinutes: 15,
      active: true
    });
    setIsModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (item) => () => {
      setSelectedAutomode(item);
      form.resetFields();
      form.setFieldsValue({
        message: item?.message ?? '',
        intervalMinutes: item?.intervalMinutes ?? 15,
        active: item?.active ?? true
      });
      setIsModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedAutomode(null);
    form.resetFields();
  }, [form]);

  const onToggleActive = useCallback(
    (item) => (checked) => {
      dispatch(updateAutomode({ id: item?._id, payload: { ...item, active: checked } }));
    },
    [dispatch]
  );

  const onDelete = useCallback(
    (item) => () => {
      dispatch(deleteAutomode({ id: item?._id }));
    },
    [dispatch]
  );

  const columns = [
    {
      title: t('table.message'),
      dataIndex: 'message',
      key: 'message'
    },
    {
      title: t('table.interval'),
      dataIndex: 'intervalMinutes',
      key: 'intervalMinutes',
      width: 150,
      render: (value) => (
        <Tag color={INTERVAL_COLOR_MAP[value] || 'default'}>{t(`intervals.${value}`)}</Tag>
      )
    },
    {
      title: t('table.active'),
      dataIndex: 'active',
      key: 'active',
      width: 140,
      render: (active, item) => <Switch checked={active} onChange={onToggleActive(item)} />
    },
    {
      title: t('table.actions'),
      key: 'actions',
      width: 170,
      render: (_, item) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={openEditModal(item)}>
            {t('actions.edit')}
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={onDelete(item)}>
            {t('actions.delete')}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="automode">
      <div className="automode__content">
        <div className="automode__header">
          <div className="automode__hint">{t('help.live_only')}</div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t('actions.create_automode')}
          </Button>
        </div>

        <Card className="automode__card" bordered={false}>
          <Table
            rowKey={(row) => row?._id || row?.id}
            columns={columns}
            dataSource={data}
            onChange={handlePageChange}
            pagination={{
              current: automodesMeta?.page || automodePagination.page,
              pageSize: automodesMeta?.perPage || automodePagination.perPage,
              total: automodesMeta?.total || 0,
              showSizeChanger: true
            }}
            loading={isLoading}
          />
        </Card>
      </div>

      <AutomodeModal
        form={form}
        selectedAutomode={selectedAutomode}
        isModalOpen={isModalOpen}
        onCancel={closeModal}
      />
    </div>
  );
};
